import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { CalendarIcon, X } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const monthEnds = () => {
  const now = new Date();
  return Array.from({ length: 12 }, (_, i) => new Date(now.getFullYear(), now.getMonth() - i, 0));
};
const iso = (d) => format(d, "yyyy-MM-dd");

/** Previous calendar month as a {from, to} filter pair (local dates, no UTC shift). */
export const previousMonthFilters = () => {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const to = new Date(now.getFullYear(), now.getMonth(), 0);
  return { from, to };
};

export const DatePick = ({ label, value, onChange, testId }) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button variant="outline" size="sm" data-testid={testId} className="border-slate-300 text-slate-600 font-normal h-9">
        <CalendarIcon className="h-3.5 w-3.5 mr-1.5" />
        {value ? format(value, "dd MMM yyyy") : label}
      </Button>
    </PopoverTrigger>
    <PopoverContent className="w-auto p-0" align="start">
      <Calendar
        mode="single"
        selected={value}
        onSelect={onChange}
        defaultMonth={value || new Date()}
        captionLayout="dropdown-buttons"
        fromYear={2022}
        toYear={2030}
      />
    </PopoverContent>
  </Popover>
);

export const WbsPatternInput = ({ value, onChange, testId, width = "w-56" }) => {
  const [draft, setDraft] = useState(value || "");
  useEffect(() => { setDraft(value || ""); }, [value]);
  const apply = () => { if (draft.trim() !== (value || "")) onChange(draft.trim()); };
  return (
    <div className={`relative ${width}`}>
      <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && apply()} onBlur={apply}
        placeholder="WBS: 2506* or *FUR*" title="Plain code = all WBS starting with it; 2506* = starts with; *FUR* = contains; comma-separate several. Enter to apply."
        className="h-9 pr-7 border-slate-300 text-sm bg-white font-mono" data-testid={testId} />
      {draft && (
        <button type="button" onClick={() => { setDraft(""); onChange(""); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs" data-testid={testId ? `${testId}-clear` : undefined}>✕</button>
      )}
    </div>
  );
};

export const DateFilters = ({ showAsOn = false, showWbs = false, filters, setFilters, testPrefix, fromLabel = "Invoice date from", toLabel = "Invoice date to" }) => {
  const ends = monthEnds().map(iso);
  const custom = filters.asOn && filters.asOn !== "today" && !ends.includes(filters.asOn) ? filters.asOn : null;
  return (
  <div className="flex flex-wrap items-center gap-2">
    {showAsOn && (
      <Select value={filters.asOn || "today"} onValueChange={(v) => setFilters({ ...filters, asOn: v })}>
        <SelectTrigger className="w-48 h-9 border-slate-300 text-sm bg-white" data-testid={`${testPrefix}-ason-select`}>
          <SelectValue placeholder="As on" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="today">As on: Today</SelectItem>
          {custom && <SelectItem value={custom}>As on: {format(new Date(`${custom}T00:00:00`), "dd MMM yyyy")}</SelectItem>}
          {ends.map((d) => (
            <SelectItem key={d} value={d}>As on: {format(new Date(`${d}T00:00:00`), "dd MMM yyyy")}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    )}
    {showAsOn && (
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" data-testid={`${testPrefix}-ason-custom`} title="Type or pick any As-on date"
            className={`h-9 font-normal ${custom ? "border-slate-900 text-slate-900" : "border-slate-300 text-slate-600"}`}>
            <CalendarIcon className="h-3.5 w-3.5 mr-1.5" />{custom ? "Change date" : "Pick As-on date"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3" align="start">
          <AsOnTyped value={filters.asOn !== "today" ? filters.asOn : ""} onApply={(v) => setFilters({ ...filters, asOn: v })} testPrefix={testPrefix} />
          <Calendar mode="single" selected={filters.asOn && filters.asOn !== "today" ? new Date(`${filters.asOn}T00:00:00`) : undefined}
            onSelect={(d) => d && setFilters({ ...filters, asOn: iso(d) })} defaultMonth={filters.asOn && filters.asOn !== "today" ? new Date(`${filters.asOn}T00:00:00`) : new Date()}
            captionLayout="dropdown-buttons" fromYear={2022} toYear={new Date().getFullYear()} disabled={{ after: new Date() }} />
        </PopoverContent>
      </Popover>
    )}
    <DatePick label={fromLabel} value={filters.from} onChange={(d) => setFilters({ ...filters, from: d })} testId={`${testPrefix}-from-date`} />
    <DatePick label={toLabel} value={filters.to} onChange={(d) => setFilters({ ...filters, to: d })} testId={`${testPrefix}-to-date`} />
    {showWbs && <WbsPatternInput value={filters.wbs || ""} onChange={(v) => setFilters({ ...filters, wbs: v })} testId={`${testPrefix}-wbs`} />}
    {(filters.from || filters.to || filters.wbs || (filters.asOn && filters.asOn !== "today")) && (
      <Button variant="ghost" size="sm" data-testid={`${testPrefix}-clear-filters`}
              onClick={() => setFilters({ asOn: "today", from: null, to: null, wbs: "" })} className="text-slate-500 h-9">
        <X className="h-3.5 w-3.5 mr-1" /> Clear
      </Button>
    )}
  </div>
  );
};

const AsOnTyped = ({ value, onApply, testPrefix }) => {
  const [draft, setDraft] = useState(value ? format(new Date(`${value}T00:00:00`), "dd-MM-yyyy") : "");
  const [err, setErr] = useState("");
  const apply = () => {
    const m = draft.trim().match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
    const d = m && new Date(+m[3], +m[2] - 1, +m[1]);
    if (!d || d.getDate() !== +m[1] || d.getMonth() !== +m[2] - 1) return setErr("Use DD-MM-YYYY");
    if (d > new Date()) return setErr("As-on date can't be in the future");
    setErr(""); onApply(iso(d));
  };
  return (
    <div className="mb-2">
      <div className="flex gap-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && apply()} placeholder="DD-MM-YYYY"
          className="h-8 text-sm font-mono" data-testid={`${testPrefix}-ason-input`} />
        <Button size="sm" className="h-8 bg-slate-900 text-white hover:bg-slate-800" onClick={apply} data-testid={`${testPrefix}-ason-apply`}>Apply</Button>
      </div>
      {err && <p className="mt-1 text-xs text-red-600" data-testid={`${testPrefix}-ason-error`}>{err}</p>}
    </div>
  );
};

export const filterParams = (filters) => {
  const p = {};
  if (filters.asOn && filters.asOn !== "today") p.as_on = filters.asOn;
  if (filters.from) p.from_date = iso(filters.from);
  if (filters.to) p.to_date = iso(filters.to);
  if (filters.wbs && filters.wbs.trim()) p.wbs = filters.wbs.trim();
  return p;
};
